import { Container, FederatedPointerEvent, Graphics, Polygon, Rectangle } from "pixi.js";
import {EventEmitter, Subscription} from "nate-react-api-helpers";
import {FrmRefPoint} from "./FrmRefPoint";
import {FrmCalcPoint} from "./FrmCalcPoint";

export interface Disposable {
    destroy(): void;
}

// implemented by children that can hide/show their dimension annotations (e.g.
// FrmShape). Typed via an interface to avoid a DrawFrmThing <-> FrmShape import
// cycle, same as Disposable above.
export interface DimensionToggle {
    setShowDimensions(show: boolean): void;
}

// a drawn segment of a thing's outline, in its local coords.
export type OutlineSegment = {x0: number; y0: number; x1: number; y1: number};

// implemented by children that draw the thing's outline as a point chain (e.g.
// FrmShape), for a view that reads the geometry rather than the picture (the strike
// jamb reads the profile's steps). Typed via an interface like the two above.
export interface OutlineSource {
    outlineSegments(): OutlineSegment[];
}

export class DrawFrmThing {
    graphic: Container;
    lines: Graphics;

    // the outline this thing draws, when a point-chain child draws it (set by FrmShape).
    outline: OutlineSource | null = null;

    // children that need explicit teardown when this thing is destroyed (e.g. a
    // FrmShape holding FrmVar subscriptions). Tracked via an interface so we
    // avoid a DrawFrmThing <-> FrmShape import cycle.
    private disposables: Disposable[] = [];

    // set once destroy() runs. Late/async callbacks (deferred draws, a showReact
    // panel closing after the grid rebuilt, etc.) can still reach a torn-down
    // thing whose pixi objects are gone; every state-mutating method below
    // no-ops once this is true so those stragglers can't touch dead state.
    private _destroyed = false;

    get destroyed(): boolean {
        return this._destroyed;
    }

    // fires when this thing's geometry changes in a way that may alter its
    // bounding box (e.g. a dimension edit reshapes the profile). The layout
    // engine subscribes to this so a group/scene can re-flow when a child grows
    // or shrinks. Emitted by FrmShape.emitChanges(); subclasses with their own
    // geometry should emit it after mutating their graphic.
    changed = new EventEmitter<string>();

    constructor() {
        this.graphic = new Container();
        this.lines = new Graphics();
        this.graphic.addChild(this.lines);
    }

    // the display object hit-tested for interactivity, and the factory that
    // rebuilds its hit area. kept so subclasses can refresh the hit area after
    // their geometry changes (see refreshHitArea).
    private hitTarget: Container | null = null;
    private hitAreaFactory: (() => Polygon | Rectangle) | null = null;

    // whether this thing is currently "selected" — true while its click-spawned
    // React panel is open. makeInteractive flips it true on click; the owner
    // clears it from the panel's onClose (see setSelected). Subclasses read it
    // (or react via the onSelectedChange callback) to draw a selected style.
    private _selected = false;
    private onSelectedChange: ((selected: boolean) => void) | null = null;

    get selected(): boolean {
        return this._selected;
    }

    // set the selected state, notifying via onSelectedChange when it changes.
    // The owner calls setSelected(false) when its React panel closes to blur the
    // selection. No-op if the value is unchanged.
    setSelected(selected: boolean) {
        if (this._destroyed) return;
        if (this._selected === selected) return;
        this._selected = selected;
        this.onSelectedChange?.(selected);
    }

    // wire pointer interactivity onto this thing. pixi v7 needs three things to
    // hit-test and dispatch events: eventMode set to "static" (the object
    // listens but doesn't move under a stationary pointer), a cursor, and a hit
    // area. a filled Graphics is auto-hit-tested from its fill, so `hitArea` is
    // optional — pass it for non-filled or more forgiving hit shapes. The
    // callbacks stay pixi-free so this drawfrm package keeps no outside imports;
    // owners supply behaviour (recolor on hover, show a React panel on click).
    makeInteractive(opt: {
        // defaults to this.lines (the filled outline Graphics)
        target?: Container;
        // rebuilt via refreshHitArea() whenever geometry changes; omit to rely
        // on the Graphics fill for hit-testing.
        hitArea?: () => Polygon | Rectangle;
        onHover?: (hovered: boolean, e: FederatedPointerEvent) => void;
        onClick?: (e: FederatedPointerEvent) => void;
        // fired when the selected state changes (set true on click, cleared by
        // the owner via setSelected(false) when its panel closes). Subclasses
        // typically redraw here.
        onSelectedChange?: (selected: boolean) => void;
    }) {
        if (this._destroyed) return;
        const t = opt.target ?? this.lines;
        t.eventMode = "static";
        if (opt.onHover || opt.onClick) t.cursor = "pointer";

        this.hitTarget = t;
        this.hitAreaFactory = opt.hitArea ?? null;
        this.onSelectedChange = opt.onSelectedChange ?? null;
        this.refreshHitArea();

        if (opt.onHover) {
            t.on("pointerover", (e: FederatedPointerEvent) => opt.onHover!(true, e));
            t.on("pointerout", (e: FederatedPointerEvent) => opt.onHover!(false, e));
        }

        if (opt.onClick) {
            // pointertap is pixi's click: a down+up with no intervening drag, so
            // it doesn't fire when the user is panning the scene.
            t.on("pointertap", (e: FederatedPointerEvent) => {
                e.stopPropagation();
                this.setSelected(true);
                opt.onClick!(e);
            });
        }
    }

    // rebuild the hit area from the current geometry. Subclasses whose shape
    // changes (e.g. a stick resizing/mitring) should call this after redrawing,
    // since hitArea is in local, unscaled coords and won't follow geometry on
    // its own. No-op unless makeInteractive was given a hitArea factory.
    protected refreshHitArea() {
        if (this._destroyed) return;
        if (this.hitTarget && this.hitAreaFactory) {
            this.hitTarget.hitArea = this.hitAreaFactory();
        }
    }

    track<T extends Disposable>(d: T) {
        if (this._destroyed) {
            console.warn("Thing.track called after object was destroyed")
            d.destroy();
            return;
        }

        this.disposables.push(d);
        return d;
    }

    // accepts either a nate-react-api-helpers Subscription (`.cancel()`) or a
    // plain unsubscribe function (as returned by the local EventEmitter2), so
    // subclasses can track subscriptions from both emitter flavours.
    trackSubscription(sub: Subscription | (() => void)) {
        this.track({
            destroy() {
                if (typeof sub === "function") sub();
                else sub.cancel();
            }
        });
    }

    destroy() {
        if (this._destroyed) {
            console.warn("Thing.destroy called after object was destroyed")
            return;
        }

        this._destroyed = true;
        for (const d of this.disposables) d.destroy();
        this.disposables = [];
        // a parent may have already freed our graphic as part of its own tree
        // destroy (children:true). Skip the pixi free in that case — but the
        // logical cleanup above (subscriptions) must still run regardless.
        if (!this.graphic.destroyed) this.graphic.destroy({ children: true });
    }

    // destroy and detach everything added to this thing (tracked disposables and
    // child graphics), leaving it empty but still usable. Used to tear the grid
    // down for a full rebuild without discarding this container itself.
    destroyChildren() {
        if (this._destroyed) {
            console.warn("Thing.destroyChildren called after object was destroyed")
            return;
        }
        for (const d of this.disposables) d.destroy();
        this.disposables = [];
        this.lines.removeChildren();
    }

    protected makeRef(name: string, x: number, y: number) {
        const pt = new FrmRefPoint();

        // calculated point carries the door graphic so links (e.g. profile ->
        // bottomLeft/bottomRight) still reposition the door, while x/y stay
        // driven by the dynamic width/height. Use the outer `graphic` container
        // (NOT the inner `lines`): the layout (FrmSceneLayout) scales/positions
        // `graphic`, and every other anchor resolves through it too, so reading
        // `lines` here would mix local and scene space and fling the door away on
        // the next adjustOthers. `lines` sits at local (0,0)/scale 1 inside
        // `graphic`, so the corner coordinates are identical in both spaces.
        pt.setCalculatedPoint(new FrmCalcPoint(name, x, y, this.graphic));

        return pt;
    }

    add(child: DrawFrmThing) {
        if (this._destroyed) {
            console.warn("Thing.add called after object was destroyed")
            return;
        }

        this.lines.addChild(child.graphic);
        this.track(child);
    }

    reset() {
        this.destroyChildren();
        this.disposables = [];

        this.graphic.removeChildren();

        this.lines = new Graphics();
        this.graphic.addChild(this.lines);
    }
}
